import React from 'react';
import {
    Alert,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    TextField as MuiTextField,
    Tooltip,
    Typography
} from '@mui/material';
import { ContentCopy, Key } from '@mui/icons-material';

import {
    AutocompleteField,
    BoolField,
    EmailField,
    TextField
} from '../../CommonComponents/Form';
import { Popup, SectionDivider } from '../../CommonComponents';

import { fetchAllGroups, selectMyGroups } from '../../../redux/actions/groups';
import { useDispatch, useSelector } from '../../../redux/hooks';
import { fetchAllUsers, selectAllUsers } from '../../../redux/actions/users';
import { updateIntegration } from '../../../api/integrations';
import toast from '../../../utils/toast';

type SettingsType = {
    label: string,
    publishPublicly: boolean,
    showTenantName: boolean,
    groups: number[],
    creatingUserId: number,
    errorEmail: string,
    inboundToken?: string | null
}

type PropsType = {
    integrationId?: string,
    settings: Partial<SettingsType>,
    onChangeSettings: (e: Partial<SettingsType>) => void
}

export default function OpterForm({
    integrationId,
    settings,
    onChangeSettings
}: PropsType) {
    const dispatch = useDispatch();
    const groups = useSelector(selectMyGroups());
    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);
    const users = useSelector(selectAllUsers());
    const fetchUsers = React.useCallback(() => {
        dispatch(fetchAllUsers());
    }, [dispatch]);

    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [apiToken, setApiToken] = React.useState<string | null>(null);

    React.useEffect(() => {
        fetchGroups();
        fetchUsers();
    }, [fetchGroups, fetchUsers]);

    const generateToken = React.useCallback(async () => {
        if (!integrationId) {
            return;
        }
        try {
            setIsGenerating(true);
            const updated = await updateIntegration(integrationId, { regenerateToken: true });
            setApiToken(updated?.apiToken ?? null);
            setConfirmOpen(false);
            onChangeSettings({ inboundToken: '*****' });
            toast('Ny API-nyckel genererad');
        } catch (e: any) {
            toast(e, 'Kunde inte generera API-nyckel, försök igen');
        } finally {
            setIsGenerating(false);
        }
    }, [integrationId, onChangeSettings]);

    const copyToken = React.useCallback(async () => {
        if (!apiToken) {
            return;
        }
        try {
            await navigator.clipboard.writeText(apiToken);
            toast('API-nyckeln är kopierad');
        } catch {
            toast('Kunde inte kopiera, markera och kopiera nyckeln manuellt');
        }
    }, [apiToken]);

    return (
        <Grid container spacing={2}>
            <Popup
                open={confirmOpen}
                title="Generera ny API-nyckel"
                body={settings.inboundToken
                    ? 'Den nuvarande nyckeln slutar fungera direkt. Opter måste uppdateras med den nya nyckeln.'
                    : 'En API-nyckel skapas som Opter använder för att skapa uppdrag i Logic Link.'}
                okLabel="Generera"
                handleOk={generateToken}
                handleClose={() => setConfirmOpen(false)}
                isSubmitting={isGenerating}
            />

            <Popup
                open={!!apiToken}
                title="Ny API-nyckel"
                okLabel="Klar"
                handleOk={() => setApiToken(null)}
                closeOnBackdropClick={false}
                fullWidth
                maxWidth="sm"
            >
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Nyckeln visas bara en gång. Kopiera den nu och klistra in den i Opter.
                </Alert>
                <MuiTextField
                    value={apiToken ?? ''}
                    label="API-nyckel"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Kopiera">
                                    <IconButton onClick={copyToken} edge="end">
                                        <ContentCopy />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        )
                    }}
                    onFocus={(e) => e.target.select()}
                />
            </Popup>

            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionDivider label="Koppling" marginTop={2} marginBottom={1} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="label"
                            label="Namn"
                            helperText="Ett namn på kopplingen, t.ex. vilken Opter-miljö den används av"
                            value={settings.label || ''}
                            onChange={onChangeSettings}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <EmailField
                            name="errorEmail"
                            label="E-postadress för felmeddelanden"
                            value={settings.errorEmail || ''}
                            onChange={onChangeSettings}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {settings.inboundToken
                                ? 'En API-nyckel finns. Generera en ny om den behöver bytas ut.'
                                : 'Ingen API-nyckel har genererats ännu.'}
                        </Typography>
                        <Tooltip title={integrationId ? '' : 'Spara integrationen först'}>
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<Key />}
                                    onClick={() => setConfirmOpen(true)}
                                    disabled={!integrationId || isGenerating}
                                >
                                    Generera ny API-nyckel
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <SectionDivider label="Grupper och användare" marginTop={2} marginBottom={1} />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                            label="Publicera i grupp"
                            helperText="Välj grupper eller publicera publikt"
                            optionIdKey="id"
                            fetchOptions={fetchGroups}
                            options={groups.filter(
                                (o: { isOwner: boolean; canPublish: boolean; }) => (o.isOwner || o.canPublish)
                            )}
                            getOptionName={(o) => o.name}
                            getOptionDescription={(o) => o.ownerTenantName || ''}
                            onChange={(g) => onChangeSettings({
                                groups: g.map((group: any) => group.id),
                                publishPublicly: g.length > 0 ? false : undefined
                            })}
                            fetchOnlyOnce
                            displayOption={groups.filter((g) => settings.groups?.includes(g.id)) || []}
                            multiple
                            required={!settings.publishPublicly}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutocompleteField<{ id: number, firstName: string, lastName: string, title?: string }>
                            label="Publicera som användare"
                            helperText="Välj användare att publicera uppdrag som"
                            optionIdKey="id"
                            fetchOptions={fetchUsers}
                            options={users}
                            getOptionName={(o) => `${o.firstName} ${o.lastName}`}
                            getOptionDescription={(o) => o.title || ''}
                            onChange={(user) => {
                                if (user?.id) {
                                    onChangeSettings({
                                        creatingUserId: user.id
                                    });
                                }
                            }}
                            fetchOnlyOnce
                            disableClearable
                            displayOption={users.find((u) => settings.creatingUserId === u.id) || null}
                            required
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SectionDivider label="Publicering" marginTop={2} marginBottom={1} />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <BoolField
                            name="publishPublicly"
                            label="Publicera publikt"
                            helperText="Välj om uppdrag ska publiceras publikt eller endast i valda grupper."
                            value={settings.publishPublicly || false}
                            onChange={() => onChangeSettings({
                                publishPublicly: !settings.publishPublicly,
                                groups: !settings.publishPublicly ? [] : settings.groups
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <BoolField
                            name="showTenantName"
                            label="Visa företagsnamn"
                            helperText="Välj om ni vill visa ert företagsnamn på de uppdrag som publiceras."
                            value={settings.showTenantName || false}
                            onChange={onChangeSettings}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Om uppdraget publiceras direkt eller sparas som utkast väljs per uppdrag i Opter.
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
